import { ReactNode, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LessonReducer } from 'Reducers/lessonReducer';
import { Box, SxProps } from '@mui/system';
import { Typography } from '@mui/material';
import LeftHandIcon from 'assets/images/lh-icon.png';
import RightHandIcon from 'assets/images/rh-icon.png';
import Completion from './Completion';
import { CustomCircularProgress } from 'Components/StyledComponents';
import { totalPlayTimetoString } from 'Utils';
import { PlaySessionData, PreviousTierPlaySessions } from 'Types/LessonTypes';
import BarChart from 'Components/D3/TierWrapUp/BarChat';
import OvularNoteCountSymbol from 'Components/TierWrapUp/OvularNoteCountSymbol';
import { range } from 'lodash';
import { MediaQueries } from 'Types/MediaQueries';
import { mediaQueryValues } from 'Utils/MediaQueries';
import { useMediaQueries } from 'Hooks/useMediaQueries';
import { getColorByCbType } from 'Components/Colors';
import { ERROR_REC_CLASS } from 'Utils/Constants';
import { MainAppReducer } from 'Types';
import { CustomButton } from 'Components/StyledComponents';
import { useNavigate } from 'react-router-dom';
import { getPlaySessionSummary } from 'Sagas/Lesson';
import { getCurrentAuthToken } from 'Utils/Auth';
import * as lessonActions from 'Actions/lesson';
import { AppDispatch } from 'Reducers';

const Grid = ({
  children,
  mediaQueries: _mediaQueries = {
    maxHeight: mediaQueryValues.minHeight1100,
    maxWidth: mediaQueryValues.minWidth800,
  },
}: {
  children?: ReactNode;
  mediaQueries?: MediaQueries;
}) => {
  return (
    <Box
      sx={{
        justifyContent: 'center',
        gridGap: { xs: '4px', md: '10px' },
        display: 'grid',
        gridTemplateColumns: '[col1-start] 1fr  [col2-start] 1fr  [col3-start] 1fr [col3-end]',
        background: 'linear-gradient(240.8deg, #1D2F44 -56.75%, #071423 210.02%)',
        padding: { xs: '8px 6px', sm: '16px 10px', md: '24px 16px', lg: '30px 20px' },
        minHeight: { xs: '170px', md: '220px', lg: '260px' },
        height: { xs: 'auto', lg: '40%' },
        width: { xs: '96%', md: '90%', lg: '80%' },
      }}
    >
      {children}
    </Box>
  );
};

const DataCard = ({
  children,
  gridColumn,
  gridRow = '1',
  sx,
}: {
  children?: ReactNode;
  gridColumn: string;
  gridRow?: string;
  sx?: SxProps;
}) => {
  return (
    <Box
      sx={{
        background: '#2E263E',
        color: '#fff',
        borderRadius: '5px',
        padding: { xs: '4px', sm: '8px', md: '10px' },
        textAlign: 'left',
        minWidth: 0,
        gridColumn,
        gridRow,
        overflow: 'hidden',
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

type DivisorAlignment = 'vertical' | 'horizontal';

const DivisorLine = ({
  alignment = 'horizontal',
  height = '100%',
}: {
  alignment?: DivisorAlignment;
  height?: string | number;
}) => (
  <Box
    sx={{
      boxShadow: '0 0 0 0.5px rgba(206, 208, 212, 0.5)',
      width: alignment === 'horizontal' ? height : 0,
      height: alignment === 'vertical' ? height : 0,
    }}
  />
);

const InfoNumber = ({ children }: { children?: ReactNode }) => {
  return (
    <Typography
      variant="h5"
      sx={{
        color: '#6F87F1',
        paddingTop: { xs: '2px', sm: '4px', md: '8px', lg: '10px' },
        fontSize: { xs: '10pt', sm: '14pt', md: '18pt', lg: '20pt' },
      }}
    >
      {children}
    </Typography>
  );
};

const SubDataCard = ({
  children,
  withBackground = false,
  sx,
  mediaQueries: _mediaQueries = {
    maxHeight: mediaQueryValues.minHeight1100,
    maxWidth: mediaQueryValues.minWidth800,
  },
}: {
  children?: ReactNode;
  withBackground?: boolean;
  sx?: SxProps;
  mediaQueries: MediaQueries;
}) => {
  return (
    <Box
      sx={{
        background: withBackground ? '#7F81A01A' : 'none',
        marginTop: { xs: '3px', sm: '6px', md: '10px' },
        borderRadius: '5px',
        color: '#CED0D4',
        fontSize: { xs: '7pt', sm: '9pt', md: '14pt', lg: '18pt' },
        height: '45%',
        width: '100%',
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

const EmptyState = () => {
  return (
    <Grid>
      <CustomCircularProgress
        sx={{
          position: 'absolute',
          marginLeft: 'auto',
          marginRight: 'auto',
          marginTop: 'auto',
          left: 0,
          right: 0,
        }}
      />
      <DataCard gridColumn="col1-start / col2-start" />
      <DataCard gridColumn="col2-start / col3-start" />
      <DataCard gridColumn="col3-start" />
    </Grid>
  );
};

const OvularNoteCount = ({ numNotes, color }: { numNotes: number; color: string }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        '& h3': {
          marginTop: '2px',
          marginBottom: 0,
          color: color,
          fontSize: { xs: '12px', sm: '18px', md: '22px', lg: '26px' },
        },
      }}
    >
      <OvularNoteCountSymbol height={20} fill={color} />
      <h3>{numNotes}</h3>
    </Box>
  );
};

type PhraseDatum = {
  datetime: string;
  averageAccuracy: string;
  averageLHAccuracy: string;
  averageRHAccuracy: string;
};

type SessionData = {
  phraseData: PhraseDatum[];
  playTime: number;
  errorCount: number;
  totalOffTime: number;
  totalNotes: number;
};

const _testSessionData: SessionData = {
  phraseData: [
    {
      datetime: '2024-08-28T02:38:56.106Z',
      averageAccuracy: '98.02',
      averageLHAccuracy: '99.1',
      averageRHAccuracy: '97',
    },
    {
      datetime: '2024-08-28T02:39:09.459Z',
      averageAccuracy: '92.96',
      averageLHAccuracy: '85',
      averageRHAccuracy: '100',
    },
    {
      datetime: '2024-08-28T02:39:22.784Z',
      averageAccuracy: '92.34',
      averageLHAccuracy: '76',
      averageRHAccuracy: '92',
    },
    {
      datetime: '2024-08-28T02:39:36.108Z',
      averageAccuracy: '94.78',
      averageLHAccuracy: '95',
      averageRHAccuracy: '100',
    },
    {
      datetime: '2024-08-28T02:39:49.464Z',
      averageAccuracy: '97.92',
      averageLHAccuracy: '100',
      averageRHAccuracy: '97',
    },
    {
      datetime: '2024-08-28T02:40:02.780Z',
      averageAccuracy: '99.17',
      averageLHAccuracy: '98',
      averageRHAccuracy: '100',
    },
  ],
  playTime: 120013,
  errorCount: 5,
  totalOffTime: 5,
  totalNotes: 30,
};

const _playSessionData = {
  user_id: '3d1abf40-0bfb-4967-be24-2b8494e0f204',
  tier_number: 1,
  avg_running_accuracy: 53.19,
  avg_lh_running_accuracy: 57.37,
  avg_rh_running_accuracy: 48.73,
  avg_accuracy: 96,
  avg_lh_accuracy: 100,
  avg_rh_accuracy: 90.66,
  avg_error_score: 0.35,
  total_play_time: 40002,
  error_recognition_mode: 'MIDI',
  createdAt: '2025-04-27T05:48:35.863Z',
  updatedAt: '2025-04-27T05:49:32.388Z',
  deletedAt: null,
  level_number: 15,
  id: '212ce98c-3328-4031-959e-e64a169501e4',
  level_completed_event_id: null,
  tier_completed_event_id: '2d6c64a1-3c5e-4848-9401-2f0f8fbe0990',
  level_started_event_id: null,
  tier_started_event_id: null,
  tier_session_finished_event_id: null,
  semi_errors: 2,
  lh_semi_errors: 0,
  rh_semi_errors: 0,
  non_errors: 33,
  lh_non_errors: 20,
  rh_non_errors: 13,
  errors: 0,
  lh_errors: 0,
  rh_errors: 0,
  played_notes: 35,
  expected_notes: 35,
  rh_expected_notes: 15,
  lh_expected_notes: 20,
  additional_notes: 0,
  previousTiersPlaySessions: [],
};

type TierWrapUpProps = {
  mediaQueries: {
    maxHeight: mediaQueryValues.minHeight1100;
    maxWidth: mediaQueryValues.minWidth800;
  };
  debugShowTierSummary?: boolean; // NEW optional debug flag
};

const TierWrapUp = (props: TierWrapUpProps) => {
  const {
    tierSummary: tierSummaryData,
    currentUserLevelData,
    currentLevelTiers,
    lessonProcessPending,
    phrasesTilNextTier,
    tierSummaryRequestError,
  } = useSelector((state: LessonReducer) => state.lessonReducer);
  const { userData } = useSelector((state: MainAppReducer) => state.mainAppReducer);
  const levelComplete =
    currentUserLevelData?.current_tier && currentUserLevelData?.current_tier >= currentLevelTiers;
  const { mediaQueries, debugShowTierSummary = false } = props;
  const dispatch = useDispatch<AppDispatch>();
  const { maxHeight } = useMediaQueries();
  const navigate = useNavigate();
  const [debugPlaySessionId, setDebugPlaySessionId] = useState('');

  const debugSetPlaySession = useCallback(async () => {
    const jwtToken = await getCurrentAuthToken();
    if (jwtToken && debugPlaySessionId) {
      const ts: PlaySessionData = await getPlaySessionSummary(jwtToken, debugPlaySessionId);
      dispatch(lessonActions.setTierSummary(ts));
    }
  }, [debugPlaySessionId, dispatch]);
  const tierSummary = (tierSummaryData ?? debugShowTierSummary) ? _playSessionData : null;

  if (!tierSummary && !lessonProcessPending) {
    if (!tierSummaryRequestError) {
      return null;
    }

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          width: '100%',
        }}
      >
        {debugShowTierSummary && (
          <Box sx={{ color: 'white', margin: '30px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '12px' }}>
              <input
                placeholder="Play session id"
                value={debugPlaySessionId}
                onChange={(e) => setDebugPlaySessionId(e.target.value)}
                style={{
                  background: '#fff',
                  padding: '8px 12px',
                  borderRadius: 6,
                  border: '1px solid rgba(0,0,0,0.12)',
                  minWidth: 260,
                  color: '#000',
                }}
              />
              <CustomButton
                sx={{ textTransform: 'uppercase', height: '40px' }}
                onClick={async () => debugSetPlaySession()}
                disabled={!debugPlaySessionId}
              >
                Load
              </CustomButton>
              <CustomButton
                sx={{ textTransform: 'uppercase', height: '40px' }}
                onClick={() => setDebugPlaySessionId('')}
                disabled={!debugPlaySessionId}
              >
                Clear
              </CustomButton>
            </Box>
          </Box>
        )}
        {!debugShowTierSummary && (
          <h3>
            Something went wrong retrieving your wrap up data. We're working on it (sorry). If the
            problem persists, please reach out to support@museflow.ai
          </h3>
        )}
      </Box>
    );
  }

  if (tierSummary || lessonProcessPending) {
    const barChartData =
      tierSummary?.previousTiersPlaySessions
        .map((tierSession: PreviousTierPlaySessions) => {
          return {
            tier: tierSession.tier_number,
            time: tierSession.total_play_time,
          };
        })
        .concat([
          {
            tier: tierSummary.tier_number,
            time: tierSummary.total_play_time,
          },
        ])
        .concat(
          range(tierSummary.tier_number + 1, currentLevelTiers + 1).map((curr) => ({
            tier: curr,
            time: 0,
          })),
        )
        .sort((a, b) => a.tier - b.tier) || [];

    return (
      <>
        <Box
          sx={{
            marginTop: maxHeight > mediaQueryValues.minHeight900 ? '80px' : '4px',
            marginBottom: { xs: '2px', md: '10px' },
            fontWeight: '400',
            fontStyle: 'normal',
            fontSize: { xs: '16px', sm: '22px', md: '30px', lg: '34px' },
            lineHeight: { xs: '22px', sm: '28px', md: '36px', lg: '41px' },
            color: '#469F6A',
            letterSpacing: '0.01em',
            fontFamily: 'Lato',
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {debugShowTierSummary && (
            <Box sx={{ color: 'white', mb: 2 }}>
              <Box
                sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '12px' }}
              >
                <input
                  placeholder="Play session id"
                  value={debugPlaySessionId}
                  onChange={(e) => setDebugPlaySessionId(e.target.value)}
                  style={{
                    background: '#fff',
                    padding: '8px 12px',
                    borderRadius: 6,
                    border: '1px solid rgba(0,0,0,0.12)',
                    minWidth: 260,
                    color: '#000',
                  }}
                />
                <CustomButton
                  sx={{ textTransform: 'uppercase', height: '40px' }}
                  onClick={async () => debugSetPlaySession()}
                  disabled={!debugPlaySessionId}
                >
                  Load
                </CustomButton>
                <CustomButton
                  sx={{ textTransform: 'uppercase', height: '40px' }}
                  onClick={() => setDebugPlaySessionId('')}
                  disabled={!debugPlaySessionId}
                >
                  Clear
                </CustomButton>
              </Box>
            </Box>
          )}
          {phrasesTilNextTier == 0 && (
            <>
              {levelComplete
                ? 'Level'
                : `Tier ${currentUserLevelData?.current_tier} of ${currentLevelTiers}`}{' '}
              Complete!
            </>
          )}

          {phrasesTilNextTier != 0 && <>Session Wrap-Up</>}
        </Box>
        {process.env.REACT_APP_NODE_ENV !== 'prd' && (
          <Box
            sx={{
              color: 'white',
              padding: { xs: '2px 10px', md: '10px' },
              fontSize: { xs: '8px', md: '14px' },
              display: { xs: 'none', sm: 'block' },
            }}
          >
            play session summary id: {tierSummary?.id}
          </Box>
        )}
        {phrasesTilNextTier == 0 && <Completion />}
        {tierSummary !== null && (
          <>
            <Grid mediaQueries={mediaQueries}>
              <DataCard gridColumn="col1-start / col2-start">
                <SubDataCard
                  sx={{
                    paddingLeft: { xs: '6px', sm: '10px', md: '16px', lg: '20px' },
                  }}
                  mediaQueries={mediaQueries}
                >
                  TOTAL PLAY TIME
                  <InfoNumber>{totalPlayTimetoString(tierSummary.total_play_time)}</InfoNumber>
                </SubDataCard>
                <DivisorLine />
                <SubDataCard
                  withBackground={true}
                  mediaQueries={mediaQueries}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <BarChart data={barChartData} />
                </SubDataCard>
              </DataCard>
              <DataCard gridColumn="col2-start / col3-start" gridRow="1">
                <SubDataCard
                  sx={{
                    paddingLeft: { xs: '6px', sm: '10px', md: '16px', lg: '20px' },
                  }}
                  mediaQueries={mediaQueries}
                >
                  AVERAGE ACCURACY
                  <InfoNumber>{Math.round(tierSummary.avg_accuracy)}%</InfoNumber>
                </SubDataCard>
                <DivisorLine />
                <SubDataCard
                  withBackground={true}
                  sx={{
                    height: '45%',
                  }}
                  mediaQueries={mediaQueries}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                    }}
                  >
                    <Box
                      sx={{
                        height: '100%',
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: { xs: '6pt', sm: '8pt', md: '10pt' },
                      }}
                    >
                      <Box
                        component="img"
                        src={LeftHandIcon}
                        sx={{
                          width: { xs: '18px', sm: '28px', md: '36px', lg: '45px' },
                          height: { xs: '18px', sm: '28px', md: '36px', lg: '45px' },
                        }}
                      />
                      LEFT
                      <InfoNumber>
                        {tierSummary?.avg_lh_accuracy
                          ? Math.round(tierSummary?.avg_lh_accuracy) + '%'
                          : '--'}
                      </InfoNumber>
                    </Box>
                    <DivisorLine alignment="vertical" height="80%" />
                    <Box
                      sx={{
                        height: '100%',
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: { xs: '6pt', sm: '8pt', md: '10pt' },
                      }}
                    >
                      <Box
                        component="img"
                        src={RightHandIcon}
                        sx={{
                          width: { xs: '18px', sm: '28px', md: '36px', lg: '45px' },
                          height: { xs: '18px', sm: '28px', md: '36px', lg: '45px' },
                        }}
                      />
                      RIGHT
                      <InfoNumber>
                        {tierSummary?.avg_rh_accuracy
                          ? Math.round(tierSummary?.avg_rh_accuracy) + '%'
                          : '--'}
                      </InfoNumber>
                    </Box>
                  </Box>
                </SubDataCard>
              </DataCard>
              <DataCard gridColumn="col3-start">
                <SubDataCard
                  sx={{
                    paddingLeft: { xs: '6px', sm: '10px', md: '16px', lg: '20px' },
                  }}
                  mediaQueries={mediaQueries}
                >
                  NOTES PLAYED
                  <InfoNumber>{tierSummary.played_notes}</InfoNumber>
                </SubDataCard>
                <DivisorLine />
                <SubDataCard
                  withBackground={true}
                  sx={{
                    height: '45%',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0 10%',
                    width: '80%',
                  }}
                  mediaQueries={mediaQueries}
                >
                  <OvularNoteCount
                    numNotes={tierSummary.non_errors}
                    color={getColorByCbType(
                      ERROR_REC_CLASS.CORRECT,
                      userData?.colorblind_type || null,
                    )}
                  />
                  <DivisorLine alignment="vertical" height="80%" />
                  <OvularNoteCount
                    numNotes={tierSummary.semi_errors}
                    color={getColorByCbType(
                      ERROR_REC_CLASS.SEMI_ERROR,
                      userData?.colorblind_type || null,
                    )}
                  />
                  <DivisorLine alignment="vertical" height="80%" />
                  <OvularNoteCount
                    numNotes={tierSummary.errors}
                    color={getColorByCbType(
                      ERROR_REC_CLASS.ERROR,
                      userData?.colorblind_type || null,
                    )}
                  />
                </SubDataCard>
              </DataCard>
            </Grid>
            {phrasesTilNextTier !== 0 && (
              <CustomButton
                sx={{
                  marginTop: { xs: '12px', md: '20px' },
                  width: { xs: '320px', md: '260px', lg: '320px' },
                  height: { xs: '28px', md: '58px', lg: '70px' },
                  fontSize: { xs: '12px', md: '14px' },
                }}
                onClick={() => {
                  console.log('[MUS-61] TierWrapUp button → /roadmap');
                  navigate('/roadmap');
                }}
              >
                BACK TO ROADMAP
              </CustomButton>
            )}
          </>
        )}
        {tierSummary === null && lessonProcessPending && <EmptyState />}
      </>
    );
  }
  return null;
};

export default TierWrapUp;
